import { NotificationsActive } from '@mui/icons-material';
import { Box, Divider, FormControlLabel, Switch, Typography, useTheme } from '@mui/material';
import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    currentPushSubscription,
    disablePush,
    enablePush,
    needsHomeScreen,
    pushBlocked,
    PushPermissionDenied,
    pushSupported,
} from '../../../library/pushSubscription';
import ResourcesIds from '../../../resources/ResourcesIds';
import { IPushKey } from '../../../services/pushService';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import {
    pictureSectionDividerStyles,
    sectionHeaderContainerStyles,
    sectionHeaderIconStyles,
    sectionHeaderTitleStyles,
} from './profile.styles';
import { pushDescriptionStyles, pushHintStyles, pushSectionStyles } from './pushNotificationsSection.styles';

/**
 * The switch that sends this device a notification when something happens to the person's
 * appointments -- the same news as the bell, for when the app is not open.
 *
 * **It is about the device, not the account.** A phone and a desk are switched on separately,
 * the answer lives in the browser, and what the switch shows is read from the browser rather
 * than from the server: whether *this* browser holds a subscription.
 *
 * **It is absent far more often than it is present**, and each absence is deliberate:
 * - the deployment has push switched off (`GET /push/key` answers nothing) -- nothing to offer;
 * - the browser cannot do it, and is not an iPhone -- nothing the person could act on;
 * - an iPhone or iPad in a Safari tab gets the one sentence that is actionable there instead:
 *   push exists only for the app on the Home Screen.
 *
 * **Permission is asked from the click and never before**: no prompt on page load, and an
 * iPhone would not show one anyway.
 */
export const PushNotificationsSection = (): JSX.Element | null => {
    const theme = useTheme();
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const supported: boolean = pushSupported();
    const onIosTab: boolean = needsHomeScreen();

    const { data: key } = useReactQuery<IPushKey | null>(
        services.push.getKey(),
        { enabled: supported || onIosTab, staleTime: Infinity }
    );

    const subscribe = useReactMutation(services.push.subscribeMutation());
    const unsubscribe = useReactMutation(services.push.unsubscribeMutation());

    const [isOn, setIsOn] = useState<boolean>(false);
    const [isBusy, setIsBusy] = useState<boolean>(false);
    const [isBlocked, setIsBlocked] = useState<boolean>(pushBlocked());

    // What the browser holds is the truth; the server is only told about it.
    useEffect(() => {
        let cancelled = false;

        void currentPushSubscription()
            .then((subscription: PushSubscription | null) => {
                if (!cancelled) {
                    setIsOn(subscription !== null);
                }
            })
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, []);

    if (!key || (!supported && !onIosTab)) {
        return null;
    }

    const turnOn = async (): Promise<void> => {
        try {
            const subscription: PushSubscription = await enablePush(key.publicKey);
            await subscribe.mutateAsync(subscription.toJSON());
            setIsOn(true);
        } catch (error) {
            if (error instanceof PushPermissionDenied) {
                // Refused in the prompt, or blocked before it could be shown. Either way the
                // sentence under the switch is the explanation; a red toast would be a scolding.
                setIsBlocked(pushBlocked());
                return;
            }

            // Subscribed in the browser but not on the server would be a switch that says on
            // and a phone that stays silent, so the browser's half is taken back.
            await disablePush().catch(() => undefined);
            showSnackbar(translate(ResourcesIds.PUSH_FAILED), 'error');
        }
    };

    const turnOff = async (): Promise<void> => {
        const endpoint: string | null = await disablePush().catch(() => null);
        setIsOn(false);

        if (endpoint !== null) {
            // Best effort: the browser has already unsubscribed, so the server's row is dead
            // either way and is dropped the first time something is sent to it.
            unsubscribe.mutate(endpoint, { onError: () => undefined });
        }
    };

    const handleToggle = (wanted: boolean): void => {
        setIsBusy(true);
        void (wanted ? turnOn() : turnOff()).finally(() => setIsBusy(false));
    };

    return (
        <>
            <Divider sx={pictureSectionDividerStyles} />

            <Box sx={sectionHeaderContainerStyles}>
                <NotificationsActive sx={sectionHeaderIconStyles(theme)} />
                <Typography variant="h6" component="h2" sx={sectionHeaderTitleStyles}>
                    {translate(ResourcesIds.PUSH_SECTION_TITLE)}
                </Typography>
            </Box>

            <Box sx={pushSectionStyles}>
                <Typography sx={pushDescriptionStyles}>
                    {translate(ResourcesIds.PUSH_SECTION_DESCRIPTION)}
                </Typography>

                {onIosTab ? (
                    <Typography sx={pushHintStyles}>
                        {translate(ResourcesIds.PUSH_IOS_HINT)}
                    </Typography>
                ) : (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isOn}
                                    disabled={isBusy || isBlocked}
                                    onChange={(_, wanted: boolean) => handleToggle(wanted)}
                                />
                            }
                            label={translate(ResourcesIds.PUSH_SWITCH_LABEL)}
                        />
                        {isBlocked && (
                            <Typography sx={pushHintStyles}>
                                {translate(ResourcesIds.PUSH_BLOCKED)}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </>
    );
};
